/**
 * Custom Dropdown Component
 * Fancy dropdown with animations and theme support
 */

import React, { useState, useRef, useEffect } from 'react';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';

export interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  triggerStyle?: React.CSSProperties;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select option',
  label,
  triggerStyle,
}) => {
  const { theme } = useUIStore();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter options based on search
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => opt.value === value);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
    setHoveredIndex(null);
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    marginBottom: '6px',
    color: theme === 'dark' ? '#9ca3af' : '#374151',
  };

  const defaultTriggerStyle: React.CSSProperties = {
    width: '100%',
    padding: '4px 12px',
    border: `1px solid ${isOpen ? '#3A8C45' : (theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb')}`,
    borderRadius: '6px',
    background: theme === 'dark' ? 'rgba(0, 0, 0, 0.6)' : '#f9fafb',
    color: theme === 'dark' ? '#e5e7eb' : '#111827',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.2s',
    boxShadow: isOpen ? '0 0 0 2px rgba(58, 140, 69, 0.1)' : 'none',
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    background: theme === 'dark'
      ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    border: `1px solid ${theme === 'dark' ? 'rgba(58, 140, 69, 0.3)' : '#e5e7eb'}`,
    borderRadius: '8px',
    boxShadow: theme === 'dark'
      ? '0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(58, 140, 69, 0.2)'
      : '0 10px 30px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    maxHeight: '300px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'dropdownSlideIn 0.2s ease-out',
  };

  const searchContainerStyle: React.CSSProperties = {
    padding: '8px',
    borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}`,
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}`,
    borderRadius: '6px',
    background: theme === 'dark' ? 'rgba(0, 0, 0, 0.3)' : '#fff',
    color: theme === 'dark' ? '#e5e7eb' : '#111827',
    fontSize: '13px',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const optionsListStyle: React.CSSProperties = {
    maxHeight: '240px',
    overflowY: 'auto',
    padding: '4px',
  };

  const optionStyle = (isSelected: boolean, isHovered: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    fontSize: '12px',
    textAlign: 'left',
    color: isSelected
      ? (theme === 'dark' ? '#3A8C45' : '#2d7038')
      : (theme === 'dark' ? '#e5e7eb' : '#111827'),
    background: isHovered
      ? (theme === 'dark' ? 'rgba(58, 140, 69, 0.15)' : 'rgba(58, 140, 69, 0.1)')
      : isSelected
        ? (theme === 'dark' ? 'rgba(58, 140, 69, 0.1)' : 'rgba(58, 140, 69, 0.05)')
        : 'transparent',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.15s',
    fontWeight: isSelected ? 600 : 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  });

  const arrowStyle: React.CSSProperties = {
    fontSize: '10px',
    color: triggerStyle?.color ?? (theme === 'dark' ? '#64748b' : '#94a3b8'),
    transition: 'transform 0.2s',
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    flexShrink: 0,
    marginLeft: '4px',
  };

  const emptyStateStyle: React.CSSProperties = {
    padding: '20px',
    textAlign: 'center',
    color: theme === 'dark' ? '#64748b' : '#94a3b8',
    fontSize: '13px',
  };

  return (
    <>
      <style>
        {`
          @keyframes dropdownSlideIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div style={containerStyle} ref={dropdownRef}>
        {label && <label style={labelStyle}>{label}</label>}

        <div
          className="custom-dropdown-trigger"
          style={{ ...defaultTriggerStyle, ...triggerStyle }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span style={{ flex: 1 }}>
            {selectedOption?.label || placeholder}
          </span>
          <span style={arrowStyle}>▼</span>
        </div>

        {isOpen && (
          <div style={dropdownStyle}>
            {/* Search Input */}
            <div style={searchContainerStyle}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={searchInputStyle}
                onFocus={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = '#3A8C45';
                }}
                onBlur={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb';
                }}
              />
            </div>

            {/* Options List */}
            <div style={optionsListStyle}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === value;
                  const isHovered = hoveredIndex === index;

                  return (
                    <div
                      key={option.value}
                      style={optionStyle(isSelected, isHovered)}
                      onClick={() => handleSelect(option.value)}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <span>{option.label}</span>
                      {isSelected && (
                        <span style={{ fontSize: '16px', color: '#3A8C45' }}>✓</span>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={emptyStateStyle}>
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
